import { FormControl, FormLabel, Input, Sheet, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import WrappedButton from "../WrappedButton";
import "./Login.css";

/**
 * PasswordChange component - allows users to set a new password using a reset token
 * Token is passed via query string parameter 'id'
 */
export default function PasswordChange() {
	const [password, setPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [error, setError] = React.useState("");
	const [success, setSuccess] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	// Get the reset token from query string
	const urlParams = new URLSearchParams(window.location.search);
	const resetToken = urlParams.get("id");

	const handleSubmit = () => {
		setError("");

		// Validate inputs
		if (Util.isNullOrEmpty(password) || Util.isNullOrEmpty(confirmPassword)) {
			setError("Both password fields are required.");
			return;
		}

		if (password !== confirmPassword) {
			setError("Passwords do not match.");
			return;
		}

		if (password.length < 6) {
			setError("Password must be at least 6 characters.");
			return;
		}

		if (!resetToken) {
			setError("Invalid or missing reset token.");
			return;
		}

		setIsSubmitting(true);

		// Call the backend to set the new password
		fetch("User/SetNewPassword", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				password: password,
				reset: resetToken,
			}),
		})
			.then((response) => response.json())
			.then((result) => {
				setIsSubmitting(false);
				if (result === true) {
					setSuccess(true);
				} else {
					setError("Invalid reset link. Please request a new password reset.");
				}
			})
			.catch(() => {
				setIsSubmitting(false);
				setError("An error occurred. Please try again.");
			});
	};

	// Show success message after password is changed
	if (success) {
		return (
			<div className="login-wrapper">
				<Sheet
					sx={{
						mx: "auto",
						width: 300,
						px: 2,
						py: 3,
						gap: 2,
						display: "flex",
						flexDirection: "column",
						borderRadius: 0,
						boxShadow: "md",
					}}
					variant="outlined">
					<div>
						<Typography level="h4" component="h1">
							<b>Success!</b>
						</Typography>
						<Typography level="body-sm">Your password has been changed.</Typography>
					</div>
					<WrappedButton onClick={() => (window.location.href = "/login")} sx={{ mt: 1 }}>
						Go to Login
					</WrappedButton>
				</Sheet>
			</div>
		);
	}

	return (
		<div className="login-wrapper">
			<Sheet
				sx={{
					mx: "auto",
					width: 300,
					px: 2,
					py: 3,
					gap: 2,
					display: "flex",
					flexDirection: "column",
					borderRadius: 0,
					boxShadow: "md",
				}}
				variant="outlined">
				<div>
					<Typography level="h4" component="h1">
						<b>Set New Password</b>
					</Typography>
					<Typography level="body-sm">Enter your new password below.</Typography>
				</div>
				<FormControl>
					<FormLabel>New Password</FormLabel>
					<Input
						name="password"
						type="password"
						placeholder="Enter new password"
						onChange={(event) => setPassword(event.target.value)}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Confirm Password</FormLabel>
					<Input
						name="confirmPassword"
						type="password"
						placeholder="Confirm new password"
						onChange={(event) => setConfirmPassword(event.target.value)}
						onKeyUp={(event) => event.which === 13 && handleSubmit()}
					/>
				</FormControl>
				{error && (
					<Typography sx={{ p: 1, textAlign: "center" }} color="danger" variant="soft" fontSize="sm">
						{error}
					</Typography>
				)}
				<WrappedButton busy={isSubmitting} onClick={handleSubmit} sx={{ mt: 1 }}>
					Set Password
				</WrappedButton>
			</Sheet>
		</div>
	);
}
